import LegalLayout, { Section } from "../components/LegalLayout";

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated="February 2026"
      testid="privacy-page"
      intro="Your privacy matters. This policy explains what data Feel Greats collects, why we collect it, and how you can stay in control."
    >
      <Section heading="1. Who we are">
        <p>
          Feel Greats (the "Service", "we", "us") is a wellness platform operated from the United
          Kingdom and accessible at{" "}
          <a className="text-emerald-700 font-semibold" href="https://feelgreats.co.uk">
            https://feelgreats.co.uk
          </a>
          . You can reach us any time at{" "}
          <a className="text-emerald-700 font-semibold" href="mailto:support@feelgreats.co.uk">
            support@feelgreats.co.uk
          </a>
          .
        </p>
      </Section>

      <Section heading="2. What we collect">
        <p>
          We collect only what we genuinely need to give you a personalised wellness experience:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Account data</strong> — name, email and a hashed password when you sign up.
          </li>
          <li>
            <strong>Health-assessment data</strong> — age, gender, height, weight, lifestyle
            habits, conditions, goals and behaviour preference. Used solely to generate your
            report and recommendations.
          </li>
          <li>
            <strong>Engagement data</strong> — clicks, page views, weekly check-ins and referral
            activity, used to improve the service and your results.
          </li>
          <li>
            <strong>Payment data</strong> — handled entirely by Stripe; we never see or store your
            full card number. We only receive a transaction reference and amount.
          </li>
          <li>
            <strong>Device data</strong> — IP address, browser type and a per-browser anonymous ID
            (used to attribute conversions and run A/B experiments).
          </li>
        </ul>
      </Section>

      <Section heading="3. How we use your data">
        <ul className="list-disc pl-6 space-y-2">
          <li>To generate your personalised health report and product recommendations.</li>
          <li>To process payments and deliver your subscriptions.</li>
          <li>To send you account-related and product-related communications.</li>
          <li>To measure and improve the service (analytics &amp; experiments).</li>
          <li>To comply with our legal obligations and prevent fraud.</li>
        </ul>
        <p>
          We <strong>never sell your personal data</strong> to third parties.
        </p>
      </Section>

      <Section heading="4. Who we share data with">
        <p>We only share data with vetted service providers required to run Feel Greats:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Stripe</strong> — to process payments. Stripe's privacy policy applies to
            payment data.
          </li>
          <li>
            <strong>PostHog</strong> — for product analytics and experiments (EU-hosted servers).
          </li>
          <li>
            <strong>Cloud infrastructure providers</strong> — for secure database and application
            hosting.
          </li>
          <li>
            <strong>Anthropic / OpenAI</strong> — to generate AI-personalised explanations. We
            send a minimal subset of your assessment (no name, no contact info) and never your
            password or payment data.
          </li>
        </ul>
      </Section>

      <Section heading="5. Cookies &amp; tracking">
        <p>
          We use first-party cookies for authentication and a small number of analytics cookies
          (e.g. PostHog). You can clear them in your browser settings at any time. We do not run
          third-party advertising trackers.
        </p>
      </Section>

      <Section heading="6. Data retention">
        <p>
          We retain your account and assessment data for as long as your account is active, plus
          a limited period afterwards for legal and accounting requirements. You can request
          deletion any time by emailing{" "}
          <a className="text-emerald-700 font-semibold" href="mailto:support@feelgreats.co.uk">
            support@feelgreats.co.uk
          </a>
          .
        </p>
      </Section>

      <Section heading="7. Your rights (UK GDPR)">
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Access a copy of your data</li>
          <li>Correct inaccurate data</li>
          <li>Request erasure ("right to be forgotten")</li>
          <li>Object to or restrict processing</li>
          <li>Data portability (a machine-readable export)</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <p>
          To exercise any of these rights, email{" "}
          <a className="text-emerald-700 font-semibold" href="mailto:support@feelgreats.co.uk">
            support@feelgreats.co.uk
          </a>
          . We respond within 30 days. If you're not satisfied, you can complain to the UK
          Information Commissioner's Office (ICO).
        </p>
      </Section>

      <Section heading="8. Security">
        <p>
          We use industry-standard practices: TLS encryption in transit, bcrypt password hashing,
          access controls and regular security reviews. No system is 100% secure — please use a
          unique strong password and notify us immediately of any suspicious activity.
        </p>
      </Section>

      <Section heading="9. Children">
        <p>
          Feel Greats is not intended for children under 16. We do not knowingly collect personal
          data from children. If you believe we have, please contact us and we will delete it.
        </p>
      </Section>

      <Section heading="10. Changes to this policy">
        <p>
          We may update this policy from time to time. The "Last updated" date at the top will
          always reflect the most recent version. Significant changes will be communicated by
          email to active accounts.
        </p>
      </Section>
    </LegalLayout>
  );
}
